import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Store, Search, ShoppingCart } from 'lucide-react';
import { catalogApi } from '@/services/catalogApi';
import { queryKeys } from '@/lib/queryKeys';
import { formatPrice } from '@printtat/shared';
import type { CatalogProductListItem } from '@printtat/shared';
import {
  PageHeader,
  PageContent,
  EmptyState,
  LoadingState,
  Button,
} from '@printtat/ui';
import { QuickAddModal } from './QuickAddModal';

export const CatalogPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [quickAdd, setQuickAdd] = useState<CatalogProductListItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.catalog.all(), { search, page }],
    queryFn: () => catalogApi.list({ search: search || undefined, page, limit: 24 }),
  });

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <PageContent className="overflow-y-auto h-screen">
      <PageHeader
        icon={Store}
        title="Explore"
        description="Browse ready-to-buy products"
      />

      {/* Search */}
      <div className="mt-6 max-w-sm">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search catalog..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="mt-6">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={4} rows={2} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Store}
            title="No products yet"
            description={search ? 'Try a different search' : 'Check back soon for new products'}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="group border rounded-xl bg-white overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/catalog/${item.slug}`)}
                >
                  {/* Image */}
                  <div className="aspect-square bg-gray-50 relative overflow-hidden">
                    {item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt={item.displayName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Store size={32} className="text-gray-300" />
                      </div>
                    )}

                    {/* Quick add button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuickAdd(item);
                      }}
                      className="absolute bottom-3 right-3 p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm
                        opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                    >
                      <ShoppingCart size={16} className="text-gray-700" />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.displayName}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.productName}</p>
                    <p className="text-sm font-semibold text-gray-900 mt-2">
                      {formatPrice(item.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick add modal */}
      {quickAdd && (
        <QuickAddModal
          catalogProduct={quickAdd}
          onClose={() => setQuickAdd(null)}
        />
      )}
    </PageContent>
  );
};
